import { ChakraProvider, defaultSystem, VStack, Spinner } from "@chakra-ui/react"
import { ThemeProvider } from "next-themes"
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom"
import { Box, Container, HStack, Button, CloseButton, Collapsible, Drawer, Portal } from "@chakra-ui/react"
import Footer from "./components/Footer"
import { Auth } from "./components/Auth"
import { Me } from "./components/Me"
import { ColorModeButton, useColorModeValue } from "./components/ui/color-mode";
import { useState } from "react"
import { Clients } from "./components/Clients";
import { ClientDetail } from "./components/ClientDetail";
import { Payments } from "./components/Payments";
import { PaymentDetail } from "./components/PaymentDetail";
import { Teachers } from "./components/Teachers";
import { Subjects } from "./components/Subjects";
import { LessonCalendar } from "./components/LessonCalendar";
import { Lessons } from "./components/Lessons";
import { Students } from "./pages/Students";
import { PriceMatrix } from "./components/PriceMatrix";
import { TeacherRates } from "./components/TeacherRates";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { t } from "./lib/i18n";
import { MostIndebted } from "./components/MostIndebted";
import { TeacherLessonsReport } from "./components/TeacherLessonsReport";
import { Unauthorized } from "./components/Unauthorized";
import { FetchingProvider, useFetching } from "./lib/FetchingContext";
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_AUDIENCE } from "./lib/env";

function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <ChakraProvider value={defaultSystem}>
        <ThemeProvider attribute="class" >
          <BrowserRouter>
            <FetchingProvider>
              {children}
            </FetchingProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ChakraProvider>
    </Auth0Provider>
  )
}


export function App() {
  return (
    <AppProviders>
      <InnerApp />
    </AppProviders>
  )
}

function InnerApp() {
  const [isNavBarOpen, setIsNavBarOpen] = useState(false);

  return (
    <BackgroundColor>
      <Header setNavigationOpen={setIsNavBarOpen} />
      <Container pt="8" pb="8" minH="100vh">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Clients />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/me" element={<Me />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/client/:id" element={<ClientDetail />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/payment/:id" element={<PaymentDetail />} />
            <Route path="/teachers" element={<Teachers />} />
            <Route path="/students" element={<Students />} />
            <Route path="/subjects" element={<Subjects />} />
            <Route path="/reports" element={<Navigate to="/reports/most-indebted" replace />} />
            <Route path="/reports/most-indebted" element={<MostIndebted />} />
            <Route path="/reports/teacher-lessons" element={<TeacherLessonsReport />} />
            <Route path="/schedule" element={<LessonCalendar />} />
            <Route path="/lessons" element={<Lessons />} />
            <Route path="/price-matrix" element={<PriceMatrix />} />
            <Route path="/teacher-rates" element={<TeacherRates />} />
          </Routes>
        </ErrorBoundary>
      </Container>
      <Footer />
      <Navigation open={isNavBarOpen} setNavigationOpen={setIsNavBarOpen} />
    </BackgroundColor>
  );
}

function Header(props: { setNavigationOpen: (open: boolean) => void }) {
  const { isAuthenticated } = useAuth0();
  const { isFetching } = useFetching();

  const header = (
    <HStack padding="2" justify="space-between" w="full">
      <HStack gap="2">
        {isAuthenticated && (
          <Button onClick={() => props.setNavigationOpen?.(true)} size="sm">☰</Button>
        )}
        {isAuthenticated && <Breadcrumbs />}
      </HStack>
      <HStack>
        {isFetching && <Spinner size="sm" />}
        <Auth />
        <ColorModeButton />
      </HStack>
    </HStack>
  );

  const bgColor = useColorModeValue("#ecececff", "#242424")
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  return (
    <Box position="sticky" top="0" zIndex="10" bgColor={bgColor} color={textColor} opacity={"90%"} boxShadow="sm" w="full">
      {header}
    </Box>
  );
}

const Navigation = ({ open, setNavigationOpen }: { open: boolean, setNavigationOpen: (open: boolean) => void }) => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth0();

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Drawer.Root open={open} onOpenChange={(e) => setNavigationOpen(e.open)} placement="start">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>{t('nav.navigation')}</Drawer.Title>
            </Drawer.Header>
            <Drawer.Body>
              <VStack w="full" alignItems="flex-start">
                {/* Registry */}
                <Collapsible.Root width="full">
                  <Collapsible.Trigger asChild>
                    <Button w="full" variant="ghost" justifyContent="flex-start">{t('nav.registry')} ▾</Button>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <VStack w="full" alignItems="flex-start" pl="4">
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/clients'); setNavigationOpen(false); }}>{t('nav.clients')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/students'); setNavigationOpen(false); }}>{t('nav.students')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/teachers'); setNavigationOpen(false); }}>{t('nav.teachers')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/subjects'); setNavigationOpen(false); }}>{t('nav.subjects')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/price-matrix'); setNavigationOpen(false); }}>{t('nav.price_matrix')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/teacher-rates'); setNavigationOpen(false); }}>{t('nav.teacher_rates')}</Button>
                    </VStack>
                  </Collapsible.Content>
                </Collapsible.Root>

                {/* Lessons */}
                <Collapsible.Root width="full">
                  <Collapsible.Trigger asChild>
                    <Button w="full" variant="ghost" justifyContent="flex-start">{t('nav.lessons_group')} ▾</Button>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <VStack w="full" alignItems="flex-start" pl="4">
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/lessons'); setNavigationOpen(false); }}>{t('nav.lessons')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/schedule'); setNavigationOpen(false); }}>{t('nav.schedule')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/reports/teacher-lessons'); setNavigationOpen(false); }}>{t('nav.teacher_lessons_report')}</Button>
                    </VStack>
                  </Collapsible.Content>
                </Collapsible.Root>

                {/* Payments & Reports */}
                <Collapsible.Root width="full">
                  <Collapsible.Trigger asChild>
                    <Button w="full" variant="ghost" justifyContent="flex-start">{t('nav.payments_group')} ▾</Button>
                  </Collapsible.Trigger>
                  <Collapsible.Content>
                    <VStack w="full" alignItems="flex-start" pl="4">
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/payments'); setNavigationOpen(false); }}>{t('nav.payments')}</Button>
                      <Button w="full" variant="ghost" size="sm" justifyContent="flex-start" onClick={() => { navigate('/reports/most-indebted'); setNavigationOpen(false); }}>{t('nav.debt_report')}</Button>
                    </VStack>
                  </Collapsible.Content>
                </Collapsible.Root>
              </VStack>
            </Drawer.Body>
            <Drawer.CloseTrigger asChild >
              <CloseButton size="xs" variant="solid" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

function BackgroundColor({ children }: { children: React.ReactNode }) {
  const bgColor = useColorModeValue("#ecececff", "#242424")
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  return (
    <Box bgColor={bgColor} color={textColor} transition="background-color 0.2s" height="full">
      {children}
    </Box>
  )
}

export default App;
